import { useEffect, useRef } from "react"

interface Sparkle {
  id: number
  x: number
  y: number
  size: number
  vx: number
  vy: number
  life: number
  maxLife: number
}

export default function MouseSparkles() {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const sparklesRef = useRef<Sparkle[]>([])
  const mouseRef = useRef({ x: 0, y: 0 })
  const lastMouseRef = useRef({ x: 0, y: 0 })
  const animationRef = useRef<number>()
  const lastSparkleTime = useRef(0)
  const isMovingRef = useRef(false)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext("2d")
    if (!ctx) return

    // Set canvas size
    const updateCanvasSize = () => {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
    }
    updateCanvasSize()
    window.addEventListener("resize", updateCanvasSize)

    // Track mouse position and movement
    const handleMouseMove = (e: MouseEvent) => {
      const newPos = { x: e.clientX, y: e.clientY }
      const dx = newPos.x - mouseRef.current.x
      const dy = newPos.y - mouseRef.current.y
      const distance = Math.sqrt(dx * dx + dy * dy)
      
      isMovingRef.current = distance > 1
      mouseRef.current = newPos
    }
    window.addEventListener("mousemove", handleMouseMove)

    let sparkleId = 0

    // Animation loop
    const animate = (timestamp: number) => {
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      // Create new sparkles only when mouse is moving
      if (isMovingRef.current && timestamp - lastSparkleTime.current > 50) {
        sparklesRef.current.push({
          id: sparkleId++,
          x: mouseRef.current.x + (Math.random() - 0.5) * 15,
          y: mouseRef.current.y + (Math.random() - 0.5) * 15,
          size: Math.random() * 1.5 + 0.5,
          vx: (Math.random() - 0.5) * 1,
          vy: (Math.random() - 0.5) * 1,
          life: 1,
          maxLife: Math.random() * 25 + 15,
        })
        lastSparkleTime.current = timestamp
      }

      // Update and draw sparkles
      sparklesRef.current = sparklesRef.current.filter((sparkle) => {
        sparkle.life++
        sparkle.x += sparkle.vx
        sparkle.y += sparkle.vy
        
        const progress = sparkle.life / sparkle.maxLife
        const opacity = 1 - progress

        if (progress >= 1) return false

        // Draw sparkle as a star
        ctx.save()
        ctx.translate(sparkle.x, sparkle.y)
        
        // Draw a small glowing star
        ctx.shadowBlur = 6
        ctx.shadowColor = `rgba(249, 187, 114, ${opacity * 0.6})`
        
        // Draw cross shape for star
        ctx.strokeStyle = `rgba(249, 187, 114, ${opacity * 0.7})`
        ctx.lineWidth = sparkle.size * 0.8
        ctx.lineCap = "round"
        
        ctx.beginPath()
        ctx.moveTo(-sparkle.size * 2, 0)
        ctx.lineTo(sparkle.size * 2, 0)
        ctx.stroke()
        
        ctx.beginPath()
        ctx.moveTo(0, -sparkle.size * 2)
        ctx.lineTo(0, sparkle.size * 2)
        ctx.stroke()
        
        // Diagonal lines for sparkle effect
        ctx.beginPath()
        ctx.moveTo(-sparkle.size * 1.5, -sparkle.size * 1.5)
        ctx.lineTo(sparkle.size * 1.5, sparkle.size * 1.5)
        ctx.stroke()
        
        ctx.beginPath()
        ctx.moveTo(sparkle.size * 1.5, -sparkle.size * 1.5)
        ctx.lineTo(-sparkle.size * 1.5, sparkle.size * 1.5)
        ctx.stroke()
        
        ctx.restore()

        return true
      })

      animationRef.current = requestAnimationFrame(animate)
    }

    animationRef.current = requestAnimationFrame(animate)

    return () => {
      window.removeEventListener("resize", updateCanvasSize)
      window.removeEventListener("mousemove", handleMouseMove)
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [])

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-50"
      style={{ mixBlendMode: "screen" }}
    />
  )
}
